import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Collapse, IconButton, LinearProgress, Chip, Button, CircularProgress } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { cancelAgingImport, getAgingScoringProgress, getLatestAgingImport } from "../api/companyApi";
import { publish, subscribe, unsubscribe } from "../events";

// On-page import log for the AR dashboard: shows the most recent aging import's REAL backend
// progress (lines stored, customers created, errors) and, if it failed, exactly why. Polls while
// a run is still processing so the user watches customers populate without re-opening the modal.
const STATUS_META = {
  processing: { label: "Processing", color: "#2f57c4", bg: "#eef3ff" },
  completed: { label: "Completed", color: "#1b7f4b", bg: "#e9f8f0" },
  failed: { label: "Failed", color: "#b3261e", bg: "#fdecea" },
  stalled: { label: "Needs attention", color: "#b45309", bg: "#fff7ed" },
  cancelled: { label: "Stopped", color: "#8a6d3b", bg: "#fcf3e3" },
};

const fmt = (n) => (n == null ? null : Number(n).toLocaleString());
const timeAgo = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return ""; }
};

export default function AgingImportLog({ tenantId }) {
  const [imp, setImp] = useState(null);
  const [open, setOpen] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [scoring, setScoring] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!tenantId) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const d = await getLatestAgingImport(tenantId);
        if (!alive) return;
        setImp(d);
        setLoaded(true);
        let scoreProgress = null;
        if (d?.id && d.status !== "failed" && d.status !== "stalled" && d.status !== "cancelled") {
          try { scoreProgress = await getAgingScoringProgress(d.id); } catch { /* retry next tick */ }
        }
        if (!alive) return;
        setScoring(scoreProgress);
        const scoringActive = Number(scoreProgress?.total || 0) > Number(scoreProgress?.scored || 0);
        if (d && (d.status === "processing" || !d.status || scoringActive)) {
          // The table must refresh even if this browser's websocket is attached to another pod.
          publish("ar_dashboard_refresh", { tenantId, importId: d.id });
        }
        // Always poll: this component normally mounts before a new upload exists. Stopping after an
        // empty result was why a newly started import never appeared in the log.
        timer.current = setTimeout(tick, 3000);
      } catch {
        if (alive) {
          setLoaded(true);
          timer.current = setTimeout(tick, 3000);
        }
      }
    };
    const refreshNow = () => { if (timer.current) clearTimeout(timer.current); tick(); };
    subscribe("ar_import_started", refreshNow);
    subscribe("ar_import_progress", refreshNow);
    tick();
    return () => {
      alive = false;
      unsubscribe("ar_import_started", refreshNow);
      unsubscribe("ar_import_progress", refreshNow);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [tenantId]);

  const killProcess = async (event) => {
    event.stopPropagation();
    if (!imp?.id || cancelling) return;
    const confirmed = window.confirm("Kill this import? This stops processing and rolls back the AR lines and companies created by this upload.");
    if (!confirmed) return;
    setCancelling(true);
    try {
      const result = await cancelAgingImport(imp.id, tenantId);
      setImp((current) => ({ ...current, status: "cancelled", stageDetail: `Rolled back ${Number(result?.recordsDeleted || 0).toLocaleString()} AR lines and ${Number(result?.companiesDeleted || 0).toLocaleString()} companies.` }));
      publish("ar_dashboard_refresh", { tenantId, importId: imp.id });
    } finally {
      setCancelling(false);
    }
  };

  // Nothing to show until we've confirmed there is at least one import for this tenant.
  if (!loaded || !imp) return null;

  const scoringActive = Number(scoring?.total || 0) > Number(scoring?.scored || 0);
  const processing = imp.status === "processing" || !imp.status || scoringActive;
  const meta = processing ? { ...STATUS_META.processing, label: scoringActive && imp.status === "completed" ? "Underwriting" : STATUS_META.processing.label } : (STATUS_META[imp.status] || STATUS_META.processing);
  const created = Number(imp.companiesCreated || 0);
  const updated = Number(imp.companiesUpdated || 0);
  const errors = Number(imp.companyErrors || 0);
  const totalRows = Number(imp.rowsTotal || (Number(imp.agingRowsRead || 0) + Number(imp.paymentRowsRead || 0)) || 0);
  const rowsProcessed = Number(imp.rowsProcessed || imp.linesStored || 0);
  const progress = totalRows ? Math.min(100, Math.round((rowsProcessed / totalRows) * 100)) : Number(imp.progressPercent || 0);

  const lines = [];
  if (imp.agingRowsRead != null) lines.push(`Read ${fmt(imp.agingRowsRead)} rows from the uploaded file.`);
  if (imp.linesStored != null) lines.push(`Stored ${fmt(imp.linesStored)} AR line records.`);
  if (imp.uniqueAccounts != null) lines.push(`Derived ${fmt(imp.uniqueAccounts)} unique customers.`);
  if (imp.companiesCreated != null) lines.push(`Customers: ${created} created, ${updated} updated${errors ? `, ${errors} failed` : ""}.`);
  if (processing && imp.stageDetail) lines.push(imp.stageDetail);
  if (imp.status === "completed") lines.push("Scoring runs per customer — the table below fills in as each score completes.");

  return (
    <Box sx={{ mb: 3, border: "1px solid #e3e8f5", borderRadius: "14px", overflow: "hidden", background: "#fff" }}>
      <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.5, background: meta.bg, cursor: "pointer" }} onClick={() => setOpen((o) => !o)}>
        <Typography sx={{ fontFamily: "Rubik, sans-serif", fontWeight: 600, color: "#2F3D63", fontSize: 16 }}>
          AR Import Log
        </Typography>
        <Chip size="small" label={meta.label} sx={{ background: meta.color, color: "#fff", fontWeight: 600, height: 22 }} />
        <Typography sx={{ ml: 0.5, color: "#64748b", fontSize: 13, fontFamily: "Rubik, sans-serif" }}>
          {timeAgo(imp.completedAt || imp.createdAt)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {processing && (
          <Button size="small" color="error" variant="outlined" onClick={killProcess} disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={13} color="inherit" /> : null}
            sx={{ textTransform: "none", fontWeight: 700 }}>
            {cancelling ? "Killing…" : "Kill process"}
          </Button>
        )}
        <IconButton size="small">{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
      </Box>
      {processing && <LinearProgress variant={totalRows ? "determinate" : "indeterminate"} value={progress} sx={{ height: 4 }} />}
      <Collapse in={open}>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {(imp.status === "failed" || imp.status === "stalled") && (
            <Box sx={{ p: 1.5, mb: 0.5, borderRadius: "10px", background: "#fdecea", border: "1px solid #f5c6c2" }}>
              <Typography sx={{ color: "#b3261e", fontWeight: 600, fontSize: 13.5, fontFamily: "Rubik, sans-serif" }}>
                {imp.status === "stalled" ? "Import stalled" : "Import stopped"}: {imp.error || "unknown reason"}
              </Typography>
            </Box>
          )}
          {processing && totalRows > 0 && (
            <Box sx={{ mb: 0.75 }}>
              <Typography sx={{ color: "#334155", fontSize: 13.5, fontWeight: 600, fontFamily: "Rubik, sans-serif" }}>
                {rowsProcessed.toLocaleString()} of {totalRows.toLocaleString()} rows processed · {progress}%
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 12.5, fontFamily: "Rubik, sans-serif" }}>
                Last backend activity: {timeAgo(imp.lastHeartbeatAt || imp.updatedAt)}
              </Typography>
            </Box>
          )}
          {lines.map((l, i) => (
            <Typography key={i} sx={{ color: "#334155", fontSize: 13.5, fontFamily: "monospace" }}>
              • {l}
            </Typography>
          ))}
          {Number(scoring?.total || 0) > 0 && (
            <Box sx={{ mt: 0.75 }}>
              <Typography sx={{ color: "#334155", fontSize: 13.5, fontWeight: 600, fontFamily: "Rubik, sans-serif", mb: 0.5 }}>
                Underwriting: {Number(scoring.scored || 0).toLocaleString()} of {Number(scoring.total || 0).toLocaleString()} companies completed
              </Typography>
              <LinearProgress variant="determinate" value={Math.min(100, Math.round((Number(scoring.scored || 0) / Number(scoring.total || 1)) * 100))} sx={{ height: 7, borderRadius: 4 }} />
              {Array.isArray(scoring.inProgressNames) && scoring.inProgressNames.length > 0 && (
                <Typography sx={{ color: "#64748b", fontSize: 12.5, fontFamily: "monospace", mt: 0.5 }}>
                  Running now: {scoring.inProgressNames.slice(0, 5).join(", ")}{scoring.inProgressNames.length > 5 ? "…" : ""}
                </Typography>
              )}
            </Box>
          )}
          {Array.isArray(imp.recentCompanies) && imp.recentCompanies.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Typography sx={{ color: "#64748b", fontSize: 12.5, fontWeight: 600, fontFamily: "Rubik, sans-serif", mb: 0.25 }}>
                Latest companies added to the AR table:
              </Typography>
              {imp.recentCompanies.map((company, index) => (
                <Typography key={`${company.name}-${index}`} sx={{ color: company.action === "failed" ? "#b3261e" : "#334155", fontSize: 12.5, fontFamily: "monospace" }}>
                  {company.action === "failed" ? "✕" : "✓"} {company.name} — {company.action}
                </Typography>
              ))}
            </Box>
          )}
          {Array.isArray(imp.errorDetails) && imp.errorDetails.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Typography sx={{ color: "#8a6d3b", fontSize: 12.5, fontWeight: 600, fontFamily: "Rubik, sans-serif", mb: 0.5 }}>
                First customer errors:
              </Typography>
              {imp.errorDetails.slice(0, 5).map((e, i) => (
                <Typography key={i} sx={{ color: "#8a6d3b", fontSize: 12, fontFamily: "monospace" }}>— {String(e)}</Typography>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
